import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

/**
 * Honor a `?organization_id=` deep link on org-scoped dashboards so shared
 * URLs land on the right tenant automatically.
 *
 * - Returns the org id parsed from the URL (or null).
 * - For super admins, aligns the global "Previewing as …" org-preview banner
 *   (via setViewAsOrg) so the whole app shell — and every subsequent API call,
 *   which carries the X-View-As-Org header — matches the shared link. The org
 *   display name is resolved from /rbac/organizations so the banner shows a
 *   real name, not a raw id.
 * - Non-super-admins are intentionally left untouched: the backend tenant
 *   guard (_scope_org / X-View-As-Org handling) already coerces them to their
 *   own organization, so there is no cross-tenant exposure.
 *
 * Pages typically use the return value to seed their org state, e.g.:
 *   const urlOrgId = useDeepLinkOrg();
 *   const [selectedOrg, setSelectedOrg] = useState(urlOrgId || viewAsOrg?.id || '');
 */
export function useDeepLinkOrg() {
  const { viewAsOrg, setViewAsOrg, rbac } = useAuth();
  const [searchParams] = useSearchParams();
  const urlOrgId = searchParams.get('organization_id');
  const isSuper = !!rbac?.is_super_admin;

  useEffect(() => {
    if (!urlOrgId || !isSuper || viewAsOrg?.id === urlOrgId) return;
    let cancelled = false;
    api.get('/rbac/organizations')
      .then((r) => {
        if (cancelled) return;
        const name = (r.data.organizations || []).find((o) => o.id === urlOrgId)?.name || urlOrgId;
        setViewAsOrg(urlOrgId, name);
      })
      .catch(() => { if (!cancelled) setViewAsOrg(urlOrgId, urlOrgId); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrgId, isSuper]);

  return urlOrgId;
}
